import React from "react";
import { UscisFee, UscisFeeBreakdown } from "../../types/application";
import { formatCurrency } from "../../help/Utils";

interface UscisFeeSummaryProps {
  uscisFee: UscisFee;
  showTitle?: boolean;
  showStatus?: boolean;
}

const UscisFeeSummary: React.FC<UscisFeeSummaryProps> = ({
  uscisFee,
  showTitle = true,
  showStatus = true,
}) => {
  if (!uscisFee || uscisFee.status === "not_applicable" || uscisFee.status === "skipped") {
    return null;
  }

  const { breakdown, amount, currency, formGroup } = uscisFee;

  return (
    <div className="border border-[#E5E7EB] rounded-2xl p-6 bg-white">
      {showTitle && (
        <h3 className="text-xl font-bold text-[#1B365D] mb-4">
          USCIS Fee Summary
        </h3>
      )}

      {formGroup && (
        <div className="mb-4">
          <p className="text-sm text-gray-600">Form Group</p>
          <p className="text-base font-semibold text-[#1B365D]">{formGroup}</p>
        </div>
      )}

      {breakdown && (
        <div className="space-y-3 mb-4">
          <div className="flex justify-between items-center">
            <span className="text-[#1B365D]">Base Fee:</span>
            <span className="font-semibold text-[#1B365D]">
              {formatCurrency(breakdown.base)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-[#1B365D]">Biometrics Fee:</span>
            <span className="font-semibold text-[#1B365D]">
              {formatCurrency(breakdown.biometrics)}
            </span>
          </div>
          <div className="border-t border-gray-200 pt-3 mt-3">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold text-[#1B365D]">Total:</span>
              <span className="text-xl font-bold text-[#1B365D]">
                {formatCurrency(breakdown.total)}
              </span>
            </div>
          </div>
        </div>
      )}

      {!breakdown && amount && (
        <div className="mb-4">
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-[#1B365D]">Total Fee:</span>
            <span className="text-xl font-bold text-[#1B365D]">
              {formatCurrency(amount)}
            </span>
          </div>
        </div>
      )}

      {breakdown && breakdown.notes && breakdown.notes.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm font-semibold text-[#1B365D] mb-2">Notes:</p>
          <ul className="space-y-1">
            {breakdown.notes.map((note, index) => (
              <li key={index} className="text-sm text-gray-600 flex items-start">
                <span className="mr-2">•</span>
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showStatus && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Status: <span className="font-semibold capitalize">{uscisFee.status}</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default UscisFeeSummary;
